import React from "react";
import { Smartphone, CreditCard, Wifi, BatteryCharging, ShieldCheck, ShoppingCart, Package, Play } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import SEO from "../components/SEO";

const methods = ["NFC / Tap", "Chip & PIN", "Magnetic Stripe", "Apple Pay", "Google Pay"];

const steps = [
  { icon: ShoppingCart, title: "Choose to buy or rent", desc: "Buy your Mobile POS outright, or rent it month to month, whichever fits your masjid's budget." },
  { icon: Package, title: "Unbox & connect", desc: "Pairs with Wi-Fi or 4G/LTE in minutes. No IT team or cabling required." },
  { icon: Play, title: "Start collecting", desc: "Walk the floor, greet a guest, or run a fundraiser table and take donations anywhere in the masjid." },
];

const features = [
  { icon: Smartphone, title: "Handheld & Countertop", desc: "Take it to fundraiser tables, Jummah, or Eid gatherings, or dock it at a front desk." },
  { icon: CreditCard, title: "Every Payment Method", desc: "Tap, chip, swipe, and digital wallets like Apple Pay and Google Pay, all built in." },
  { icon: Wifi, title: "Always Connected", desc: "Wi-Fi, 4G/LTE, or Ethernet through the optional hub, so it works even off your masjid's network." },
  { icon: BatteryCharging, title: "All-Day Battery", desc: "Up to 15 hours of active use on a single charge, ready for a full day of events." },
  { icon: ShieldCheck, title: "Bank-Grade Security", desc: "End-to-end encryption and PCI P2PE-ready, the same standard used by major retailers." },
];

const specs = [
  ["Device", "Stripe Reader S710"],
  ["Display", '5.5" IPS LCD touchscreen, 1920 x 1080, Gorilla Glass'],
  ["Processor", "Qualcomm SM6125"],
  ["Memory", "4 GB RAM / 64 GB Storage"],
  ["OS", "Android 10"],
  ["Payments", "EMV chip, contactless (tap) & digital wallets, magnetic stripe"],
  ["Connectivity", "Wi-Fi, 4G/LTE, Bluetooth, Ethernet (via optional dock/hub)"],
  ["Battery", "Up to 15 hours active use, 140 hours standby, 2.5-hour charge time"],
  ["Dimensions", '6.4" x 3.2" x 0.9" (161.9 x 81.6 x 21.4 mm), approx. 318g'],
  ["Security", "End-to-end encryption, PCI P2PE ready"],
];

export default function MobilePos() {
  return (
    <div>
      <SEO
        title="Mobile POS (Stripe Reader S710) | Masjid Suite"
        description="A handheld Stripe Reader S710 card reader for taking donations anywhere in your masjid. Accepts tap, chip, swipe, and digital wallets. Buy outright or rent month to month."
        path="/mobile-pos"
      />
      <PageHero
        eyebrow="Masjid Suite Mobile POS"
        title="Take Donations"
        gradient="Anywhere in Your Masjid."
        lead={<>Built on the Stripe Reader S710, our Mobile POS turns any staff member or volunteer into a mobile donation point, at the entrance, a fundraiser table, or a community event. The software is free. The reader itself is a one-time purchase or a monthly rental.</>}
        cta={<><CTAButton to="/contact">Get Started Free</CTAButton><CTAButton to="#specs" variant="secondary">View Specs</CTAButton></>}
      />

      <div className="bg-section-alt border-y border-[#e6e7eb] py-6">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-3 text-[13px] text-[#4b5563]">
            <span className="mr-2">Accepts every way your community gives:</span>
            {methods.map((m, i) => (<span key={i} className="px-3 py-1 rounded-md glass-light text-xs font-semibold text-[#1d1d1f]">{m}</span>))}
          </div>
        </Container>
      </div>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="flex justify-center">
            <GlassCard hover={false} className="p-8 max-w-md w-full">
              <div className="aspect-[900/1426] rounded-2xl overflow-hidden border border-white/70">
                <img src="/assets/pos.png" alt="Masjid Suite Mobile POS, Stripe Reader S710" className="w-full h-full object-cover" draggable={false} />
              </div>
              <div className="mt-4 text-center text-[12px] text-[#6b7280] uppercase tracking-widest">Mobile POS, Stripe Reader S710</div>
            </GlassCard>
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="in 3 Steps">Up and Running</H2>
            <div className="mt-6 flex justify-center"><Lead>From order to your first tap-to-donate in under a week.</Lead></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => { const Icon = s.icon; return (
              <GlassCard key={i}>
                <div className="text-[#a8862f] font-display text-4xl font-semibold mb-3">0{i + 1}</div>
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Icon size={20} /></div>
                <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">{s.title}</h3>
                <p className="text-[14px] text-[#4b5563] leading-relaxed">{s.desc}</p>
              </GlassCard>
            ); })}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="Designed for People.">Built for Masjids.</H2>
            <div className="mt-6 flex justify-center"><Lead>Every detail engineered for the unique needs of Islamic communities.</Lead></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => { const Icon = f.icon; return (
              <GlassCard key={i}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22} /></div>
                <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{f.title}</h3>
                <p className="text-[13px] text-[#4b5563] leading-relaxed">{f.desc}</p>
              </GlassCard>
            ); })}
          </div>
        </Container>
      </Section>

      <Section id="specs" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <Eyebrow>Specs</Eyebrow>
            <H2 gradient="Specifications">Technical</H2>
            <div className="mt-6 flex justify-center"><Lead>The Stripe Reader S710, the same enterprise-grade hardware trusted by major retailers.</Lead></div>
          </div>
          <GlassCard hover={false} className="p-0 overflow-hidden">
            <div className="divide-y divide-emerald-100">
              {specs.map(([k, v], i) => (
                <div key={i} className="grid grid-cols-3 gap-4 px-6 py-4 text-[14px]">
                  <div className="text-[#6b7280] font-medium">{k}</div>
                  <div className="col-span-2 text-[#1d1d1f]">{v}</div>
                </div>
              ))}
            </div>
          </GlassCard>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="to your masjid?">Ready to bring Mobile POS</H2>
            <div className="mt-6 flex justify-center"><Lead>Join masjids transforming their communities with modern technology. Our team will guide you every step of the way.</Lead></div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Get Started Free</CTAButton>
              <CTAButton to="/pricing" variant="secondary">View Hardware & Pricing</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
